<template>
  <div class="page-container">
    <div class="content-wrapper">
      <h1 class="main-title">魔法秘境-英语</h1>
      <div class="section">
        <h2>下载APP</h2>
        <p class="description">请选择您的手机系统类型进行下载</p>
        
        <div class="download-buttons">
          <template v-if="isIOS">
            <van-button
              round
              block
              type="primary"
              class="download-btn"
              @click="openAppStore"
              icon="apps-o"
              :loading="loading"
            >
              在 App Store 中打开
            </van-button>
            <div class="tip">即将跳转至 App Store</div>
            <div class="version-info">
              版本 {{ iosVersion }}
            </div>
          </template>
          <template v-else>
            <van-button
              round
              block
              type="primary"
              class="download-btn"
              @click="downloadApk"
              icon="down"
              :loading="downloading"
              :disabled="downloading"
            >
              <template v-if="downloading">
                <span class="loading-text">下载中 {{ progress }}%</span>
              </template>
              <template v-else>
                下载安卓版本
              </template>
            </van-button>
            <div v-if="downloading" class="progress-bar">
              <div class="progress" :style="{ width: progress + '%' }"></div>
            </div>
            <div class="tip" v-if="!downloading">
              点击下载后请注意系统提示
            </div>
            <div class="version-info">
              版本 {{ version }}
            </div>
          </template>
        </div>
      </div>

      <!-- 更新内容 -->
      <div class="update-info" v-if="updateContent.length > 0">
        <h3>更新内容</h3>
        <ul>
          <li v-for="(content, index) in updateContent" :key="index">{{ content }}</li>
        </ul>
      </div>
    </div>

    <!-- 下载确认弹窗 -->
    <van-dialog
      v-model:show="showConfirmDialog"
      title="下载提示"
      :showCancelButton="true"
      @confirm="startDownload"
    >
      <div class="dialog-content">
        <p>请确保您的网络环境良好</p>
        <p>建议使用WiFi网络下载</p>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { showToast, showDialog } from 'vant'
import { getLatestVersion } from '@/api/app'

export default {
  name: 'DownloadView',
  setup() {
    const isIOS = ref(false)
    const downloading = ref(false)
    const loading = ref(false)
    const progress = ref(0)
    const showConfirmDialog = ref(false)
    const appSize = ref(275.4)
    const downloadUrl = ref('')
    const version = ref('')
    const iosVersion = ref('')
    const updateContent = ref([])

    // 获取最新版本信息
    const fetchLatestVersion = async () => {
      try {
        const res = await getLatestVersion()
        if (res.Success && res.Data) {
          const { ApkUrl, IosUrl, Verstion, IosVerstion, UpdateContent } = res.Data
          downloadUrl.value = isIOS.value ? IosUrl : ApkUrl
          version.value = Verstion
          iosVersion.value = IosVerstion
          updateContent.value = UpdateContent
        } else {
          showToast('获取版本信息失败')
        }
      } catch (error) {
        console.error('获取版本信息失败:', error)
        showToast('获取版本信息失败')
      }
    }

    onMounted(() => {
      checkDevice()
      checkNetworkStatus()
      fetchLatestVersion()
    })

    const checkDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase()
      const platform = navigator.platform.toLowerCase()
      
      // 使用更可靠的设备检测方法
      const getMobileOperatingSystem = () => {
        // iOS detection
        if (/iPad|iPhone|iPod/.test(navigator.platform) || 
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
          return 'iOS'
        }
        
        // Android detection
        if (/android/i.test(userAgent)) {
          return 'Android'
        }
        
        // Windows detection
        if (/win/i.test(platform)) {
          return 'Windows'
        }
        
        return '其他设备'
      }

      const deviceType = getMobileOperatingSystem()
      isIOS.value = deviceType === 'iOS'
      
      // 打印设备信息到控制台
      console.log('当前设备信息：', {
        userAgent,
        platform,
        设备类型: deviceType,
        详细信息: {
          navigator平台: platform,
          是否iOS: isIOS.value,
          是否触摸设备: 'ontouchstart' in window,
          最大触摸点数: navigator.maxTouchPoints,
          屏幕信息: {
            宽度: window.screen.width,
            高度: window.screen.height
          }
        }
      })
    }

    const checkNetworkStatus = () => {
      if (navigator.connection) {
        const connection = navigator.connection
        if (connection.type === 'cellular' && connection.effectiveType !== '4g') {
          showToast('当前网络较慢，建议使用WiFi下载')
        }
      }
    }

    const openAppStore = async () => {
      loading.value = true
      try {
        // 检测是否在微信浏览器中
        const isWechat = /MicroMessenger/i.test(navigator.userAgent)
        console.log('是否在微信浏览器中:', isWechat)
        
        if (isWechat) {
          // 在微信中，提示用户在浏览器中打开
          showDialog({
            title: '提示',
            message: '微信限制了跳转功能，请点击右上角菜单，选择「在浏览器中打开」后再尝试',
            confirmButtonText: '我知道了'
          })
        } else {
          // 创建一个隐藏的a标签来触发跳转
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = downloadUrl.value
          a.target = '_blank' // 在新标签页中打开
          document.body.appendChild(a)
          a.click()
          setTimeout(() => {
            document.body.removeChild(a) // 清理DOM
          }, 100)
        }
      } catch (error) {
        console.error('跳转失败:', error)
        showToast('跳转失败，请重试')
      } finally {
        loading.value = false
      }
    }

    const downloadApk = () => {
      showConfirmDialog.value = true
    }

    const startDownload = () => {
      if (!downloadUrl.value) {
        showToast('下载链接不可用')
        return
      }

      // 检测是否在微信浏览器中
      const isWechat = /MicroMessenger/i.test(navigator.userAgent)
      console.log('是否在微信浏览器中:', isWechat)
      
      try {
        // 创建一个隐藏的a标签来触发下载
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = downloadUrl.value
        a.download = `魔法秘境英语_${version.value}.apk`
        document.body.appendChild(a)
        
        // 在微信中，可能需要提示用户在浏览器中打开
        if (isWechat) {
          showDialog({
            title: '提示',
            message: '微信限制了下载功能，请点击右上角菜单，选择「在浏览器中打开」后再下载',
            confirmButtonText: '我知道了'
          })
        } else {
          // 触发点击事件
          a.click()
          downloading.value = true
          
          // 模拟下载进度
          let currentProgress = 0
          const interval = setInterval(() => {
            if (currentProgress >= 100) {
              clearInterval(interval)
              downloading.value = false
              showToast('下载完成')
              document.body.removeChild(a) // 清理DOM
              return
            }
            currentProgress += 10
            progress.value = currentProgress
          }, 500)
        }
      } catch (error) {
        console.error('下载失败:', error)
        showToast('下载失败，请重试')
        downloading.value = false
      }
    }

    return {
      isIOS,
      downloading,
      loading,
      progress,
      showConfirmDialog,
      appSize,
      version,
      iosVersion,
      updateContent,
      openAppStore,
      downloadApk,
      startDownload
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500&family=Ma+Shan+Zheng&display=swap');

.page-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  position: relative;
  overflow: hidden;
}

.page-container::before,
.page-container::after {
  content: '';
  position: absolute;
  width: 300px;
  height: 300px;
  background-repeat: no-repeat;
  background-size: contain;
  pointer-events: none;
  z-index: 1;
}

.page-container::before {
  top: -50px;
  left: -50px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'%3E%3Cpath d='M280,150 C280,120 250,90 220,90 C190,90 170,120 190,150 C210,180 250,195 280,180 C310,165 320,135 300,105 C280,75 230,60 180,75 C130,90 90,135 110,180 C130,225 180,255 230,240 C280,225 310,180 290,135' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.3'/%3E%3Cpath d='M250,135 C250,105 220,75 190,75 C160,75 140,105 160,135 C180,165 220,180 250,165 C280,150 290,120 270,90' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.4'/%3E%3Cpath d='M220,120 C220,90 190,60 160,60 C130,60 110,90 130,120 C150,150 190,165 220,150' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.5'/%3E%3Ccircle cx='165' cy='105' r='4' fill='%232196f3' fill-opacity='0.2'/%3E%3Ccircle cx='235' cy='145' r='5' fill='%232196f3' fill-opacity='0.2'/%3E%3Ccircle cx='185' cy='165' r='3' fill='%232196f3' fill-opacity='0.2'/%3E%3C/svg%3E");
  transform: rotate(-45deg);
  animation: vineGrowLeft 3s ease-out forwards;
  opacity: 0;
}

.page-container::after {
  top: -50px;
  right: -50px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'%3E%3Cpath d='M280,150 C280,120 250,90 220,90 C190,90 170,120 190,150 C210,180 250,195 280,180 C310,165 320,135 300,105 C280,75 230,60 180,75 C130,90 90,135 110,180 C130,225 180,255 230,240 C280,225 310,180 290,135' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.3'/%3E%3Cpath d='M250,135 C250,105 220,75 190,75 C160,75 140,105 160,135 C180,165 220,180 250,165 C280,150 290,120 270,90' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.4'/%3E%3Cpath d='M220,120 C220,90 190,60 160,60 C130,60 110,90 130,120 C150,150 190,165 220,150' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.5'/%3E%3Ccircle cx='165' cy='105' r='4' fill='%232196f3' fill-opacity='0.2'/%3E%3Ccircle cx='235' cy='145' r='5' fill='%232196f3' fill-opacity='0.2'/%3E%3Ccircle cx='185' cy='165' r='3' fill='%232196f3' fill-opacity='0.2'/%3E%3C/svg%3E");
  transform: rotate(45deg);
  animation: vineGrowRight 3s ease-out forwards;
  opacity: 0;
}

@keyframes vineGrowLeft {
  0% {
    opacity: 0;
    transform: scale(0.8) rotate(-45deg) translate(-50px, -50px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(-45deg) translate(0, 0);
  }
}

@keyframes vineGrowRight {
  0% {
    opacity: 0;
    transform: scale(0.8) rotate(45deg) translate(50px, -50px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(45deg) translate(0, 0);
  }
}

.main-title {
  text-align: center;
  font-size: 28px;
  color: #333;
  margin: 24px 0;
  padding: 0 20px;
  font-family: 'Ma Shan Zheng', 'Cinzel', 'YouYuan', 'STXingkai', serif;
  font-weight: 500;
  position: relative;
  text-shadow: 0 0 6px rgba(100, 181, 246, 0.4),
               0 0 12px rgba(33, 150, 243, 0.2);
  animation: titleGlow 2s ease-in-out infinite alternate;
  letter-spacing: 1px;
}

.main-title::before,
.main-title::after {
  content: '✦';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #2196f3;
  font-size: 16px;
  opacity: 0.8;
}

.main-title::before {
  left: -25px;
}

.main-title::after {
  right: -25px;
}

@keyframes titleGlow {
  from {
    text-shadow: 0 0 6px rgba(100, 181, 246, 0.4),
                 0 0 12px rgba(33, 150, 243, 0.2);
  }
  to {
    text-shadow: 0 0 8px rgba(100, 181, 246, 0.6),
                 0 0 16px rgba(33, 150, 243, 0.3);
  }
}

.content-wrapper {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
}

.section {
  text-align: center;
  padding: 32px 24px;
}

.section h2 {
  font-size: 24px;
  margin-bottom: 16px;
  color: #333;
}

.description {
  color: #666;
  font-size: 16px;
  margin-bottom: 32px;
}

.download-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 20px;
}

.download-btn {
  height: 44px;
  font-size: 16px;
}

.tip {
  margin-top: 12px;
  color: #666;
  font-size: 14px;
}

.version-info {
  margin-top: 24px;
  color: #999;
  font-size: 14px;
}

.update-info {
  margin-top: 24px;
  padding: 20px;
  background-color: #f8f8f8;
  border-radius: 8px;
}

.update-info h3 {
  font-size: 16px;
  color: #333;
  margin-bottom: 12px;
}

.update-info ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.update-info li {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 8px;
}

.dialog-content {
  padding: 20px;
  text-align: center;
}

.dialog-content p {
  margin: 8px 0;
  color: #666;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background-color: #f5f5f5;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;
}

.progress {
  height: 100%;
  background-color: #1989fa;
  transition: width 0.3s ease;
}

.loading-text {
  margin-left: 8px;
}
</style>
